import { useEffect, useId, useState } from "react";
import Logo from "../../assets/logo.png";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { PATH } from "../../constants/navigation";
import { MAILTO_COMMERCIAL, TEL_SEGRETERIA } from "../../constants/contacts";
import { Fade } from "react-awesome-reveal";
import MobileCard from "./components/mobile-card";
import useScrollLock from "../../hooks/useScrollLock";
import Button from "../ui/button/button";
import BoxIcon from "../ui/box-icon/box-icon";

import "./header.scss";

const Header: React.FC = () => {
  const [kebabMenuOpen, setKebabMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>(PATH.home);
  const menuId = useId();
  const device = useBreakpoint();
  const location = useLocation();
  const isMobile = device === "mobile" || device === "tablet";

  const handleClickMenu = () => setKebabMenuOpen(!kebabMenuOpen);
  const closeKebabMenu = (path: string) => {
    setActiveTab(path);
    setKebabMenuOpen(false);
  };

  useEffect(() => {
    setKebabMenuOpen(false);
  }, [isMobile]);

  useEffect(() => {
    setActiveTab(location.pathname);
  }, [location.pathname]);

  useScrollLock(kebabMenuOpen);

  return (
    <header className="header" id="header">
      <Fade
        direction="down"
        duration={700}
        damping={0.1}
        triggerOnce
        className="header__fade-wrapper"
      >
        <div className="header__logo-container">
          <Fade direction="down" duration={600} triggerOnce>
            <Link to={PATH.home} onClick={() => closeKebabMenu(PATH.home)}>
              <img
                src={Logo}
                alt="Rotoinfissioni"
                className="header__logo-img"
              />
            </Link>
          </Fade>
          {!isMobile ? (
            <Fade
              direction="up"
              delay={150}
              duration={700}
              cascade
              damping={0.15}
              triggerOnce
            >
              <nav className="header__nav-desktop" aria-label="Navigazione principale">
                <NavLink
                  onClick={() => setActiveTab(PATH.poles)}
                  to={PATH.poles}
                >
                  <span
                    className={
                      activeTab === PATH.poles
                        ? "header__active-tab"
                        : undefined
                    }
                  >
                    Pali Rotoinfissi
                  </span>
                </NavLink>
                <NavLink
                  onClick={() => setActiveTab(PATH.consultancy)}
                  to={PATH.consultancy}
                >
                  <span
                    className={
                      activeTab === PATH.consultancy
                        ? "header__active-tab"
                        : undefined
                    }
                  >
                    Consulenza
                  </span>
                </NavLink>
                <NavLink
                  onClick={() => setActiveTab(PATH.about)}
                  to={PATH.about}
                >
                  <span
                    className={
                      activeTab === PATH.about
                        ? "header__active-tab"
                        : undefined
                    }
                  >
                    Chi Siamo
                  </span>
                </NavLink>
                <NavLink
                  onClick={() => setActiveTab(PATH.contacts)}
                  to={PATH.contacts}
                >
                  <span
                    className={
                      activeTab === PATH.contacts
                        ? "header__active-tab"
                        : undefined
                    }
                  >
                    Contatti
                  </span>
                </NavLink>
              </nav>
            </Fade>
          ) : (
            <>
              {!kebabMenuOpen && (
                <button
                  type="button"
                  className="header__menu-btn"
                  aria-label="Apri menu"
                  aria-controls={menuId}
                  aria-expanded={kebabMenuOpen}
                  onClick={handleClickMenu}
                >
                  <span className="header__kebab" aria-hidden />
                </button>
              )}

              {kebabMenuOpen && (
                <div className="header__mobile-menu">
                  <div className="header__mobile-content">
                    <button
                      className="header__close-btn "
                      onClick={() => setKebabMenuOpen(false)}
                    />
                    <Fade
                      direction="down"
                      duration={500}
                      triggerOnce={false}
                      style={{ height: "100%" }}
                    >
                      <div className="header__mobile-menu-body">
                        <nav
                          id={menuId}
                          className="header__mobile-nav"
                          aria-label="Menu mobile"
                        >
                          <MobileCard
                            title={"Pali Rotoinfissi"}
                            icon={"grip-lines"}
                            linkTo={PATH.poles}
                            isActiveTab={activeTab === PATH.poles}
                            onClick={() => closeKebabMenu(PATH.poles)}
                          />
                          <MobileCard
                            title={"Consulenza"}
                            icon={"clipboard"}
                            linkTo={PATH.consultancy}
                            isActiveTab={activeTab === PATH.consultancy}
                            onClick={() => closeKebabMenu(PATH.consultancy)}
                          />

                          <MobileCard
                            title={"Chi Siamo"}
                            icon={"user"}
                            linkTo={PATH.about}
                            isActiveTab={activeTab === PATH.about}
                            onClick={() => closeKebabMenu(PATH.about)}
                          />
                          <MobileCard
                            title={"Contatti"}
                            icon={"handshake"}
                            linkTo={PATH.contacts}
                            isActiveTab={activeTab === PATH.contacts}
                            onClick={() => closeKebabMenu(PATH.contacts)}
                          />
                        </nav>
                        <div className="header__mobile-contacts">
                          <Button
                            text="Chiamaci Ora"
                            type="primary"
                            iconName="phone"
                            fullWidth
                            href={TEL_SEGRETERIA}
                          />
                          <Button
                            text="Invia Email"
                            type="secondary"
                            iconName="envelope"
                            fullWidth
                            href={MAILTO_COMMERCIAL}
                          />
                          <div className="header__social-btn">
                            <a
                              href="https://www.instagram.com/rotoinfissionisrl"
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Instagram Rotoinfissioni"
                            >
                              <BoxIcon
                                icon="instagram"
                                size="medium"
                                type="primary-rounded"
                                clickable
                              />
                            </a>
                            <a
                              href="https://www.facebook.com/profile.php?id=61586756873308&locale=it_IT"
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Facebook Rotoinfissioni"
                            >
                              <BoxIcon
                                icon="facebook"
                                size="medium"
                                type="primary-rounded"
                                clickable
                              />
                            </a>
                          </div>
                        </div>
                      </div>
                    </Fade>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Fade>
    </header>
  );
};

export default Header;
